export const norm = (val) =>
  typeof val === "string" ? val.trim() : String(val ?? "").trim();

export const COMPONENT_ABBREVIATIONS = {
  "2:1 S.E Head (L/B)": "SE",
  "2:1 S.E Head (R/T)": "SE",
  "Flat Round Head (L/B)": "FR",
  "Flat Round Head (R/T)": "FR",
  "Flat Rectangular Head (L/B)": "RECT",
  "Flat Rectangular Head (R/T)": "RECT",
  "Cylindrical Shell": "CS",
  "Conical Shell": "CONE",
};

export function getComponentAbbreviation(component) {
  return COMPONENT_ABBREVIATIONS[norm(component)] || "";
}

export function isShellComponent(component) {
  return norm(component).toLowerCase().includes("shell");
}

export function isHeadComponent(component) {
  return norm(component).toLowerCase().includes("head");
}

// The tag base for UIDs. The WBS UID shown in the top panel header (next to
// Project UID) wins; the Design tab Tag No. field is the fallback, then the
// legacy TextBox48 key.
export function resolveProjectTag({ wbsUid, tagNo, legacyTag } = {}) {
  return norm(wbsUid) || norm(tagNo) || norm(legacyTag);
}

const pad2 = (n) => String(n).padStart(2, "0");

// Shell/Head UID scheme: {TAG}-SH01, {TAG}-SH02, ... for shells and
// {TAG}-HE01, {TAG}-HE02, ... for heads. `occurrence` is the 1-based index
// of the row among rows of the same kind (shells and heads count
// separately, in table order). Unknown components fall back to the legacy
// {TAG}-{ABBR} form.
export function computeShellUID(component, projectTag, occurrence = 1) {
  const tag = norm(projectTag);
  if (!tag) return "";
  const n = Math.max(1, Number(occurrence) || 1);
  if (isShellComponent(component)) return `${tag}-SH${pad2(n)}`;
  if (isHeadComponent(component)) return `${tag}-HE${pad2(n)}`;
  const abbr = getComponentAbbreviation(component);
  if (!abbr) return "";
  return `${tag}-${abbr}`;
}

// Deterministically assigns UIDs to every shell-tab row in order: shells
// count SH01, SH02, ... and heads count HE01, HE02, ... Returns the UID per
// row index ("" where neither tag nor component resolves).
export function assignShellUIDs(rows, projectTag) {
  const tag = norm(projectTag);
  let shellCount = 0;
  let headCount = 0;
  return (Array.isArray(rows) ? rows : []).map((row) => {
    if (!row || !tag) return "";
    if (isShellComponent(row.Component)) {
      shellCount += 1;
      return `${tag}-SH${pad2(shellCount)}`;
    }
    if (isHeadComponent(row.Component)) {
      headCount += 1;
      return `${tag}-HE${pad2(headCount)}`;
    }
    return computeShellUID(row.Component, tag, 1);
  });
}

export function computeNozzleUID(mark, projectTag) {
  const tag = norm(projectTag);
  const m = norm(mark);
  if (!tag || !m) return "";
  return `${tag}-${m}`;
}

// A revision's own document UID - the name it carries in the projects tree -
// is not PV-specific: Piping and Structural name their Estimation revisions
// the same way. It lives in utils/revisionUID.js (revisionDocumentUID).
